import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import styles from "./Login.module.css";
import { getDefaultRoute, toRoleArray } from "../utils/roles";
import Button from "../components/UI/Button";
import Input from "../components/UI/Input";

const Login: React.FC = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, setError, loading, setLoading, isAuthenticated, userType } =
    useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      const roles = toRoleArray(userType);
      navigate(getDefaultRoute(roles), { replace: true });
    }
  }, [isAuthenticated, userType, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (identifier === "" || password === "") {
      setError("יש להזין טלפון/שם משתמש וסיסמה");
      return;
    }

    setLoading(true);
    const { success, userType } = await login(identifier, password);

    setLoading(false);
    if (success) {
      const roles = toRoleArray(userType);
      navigate(getDefaultRoute(roles));
    } else {
      setError("טלפון/שם משתמש או סיסמה שגויים");
    }
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifier(e.target.value);
    setError("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError("");
  };

  const whatsappIcon = (
    <svg
      stroke="currentColor"
      fill="currentColor"
      stroke-width="0"
      viewBox="0 0 448 512"
      height="40px"
      width="40px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"></path>
    </svg>
  );

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.title}>כניסה לניהול מערכת </div>

        {loading ? (
          <div className={styles.loadingSpinner}></div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              color="gray"
              size="medium"
              width="100%"
              type="text"
              placeholder="טלפון או שם משתמש"
              value={identifier}
              onChange={handleIdentifierChange}
              className={styles.loginField}
            />
            <div className={styles.passwordField}>
              <Input
                color="gray"
                size="medium"
                width="100%"
                type={showPassword ? "text" : "password"}
                placeholder="סיסמה"
                value={password}
                onChange={handlePasswordChange}
                className={styles.loginField}
                style={{ paddingInlineStart: "2.5rem" }}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.5 12C3.23 8.11 7.5 5 12 5s8.77 3.11 10.5 7c-1.73 3.89-6 7-10.5 7S3.23 15.89 1.5 12z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 3l18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.58 10.58a2 2 0 002.83 2.83"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M9.88 5.09A9.77 9.77 0 0112 5c5 0 9.27 3.11 11 7a11.66 11.66 0 01-4.07 5.13M6.61 6.61C4.35 7.99 2.65 9.96 1.5 12c1.73 3.89 6 7 10.5 7a10.76 10.76 0 004.12-.81"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            <Button
              type="submit"
              color="blue"
              size="medium"
              width="100%"
              className={styles.button}
            >
              כניסה
            </Button>
          </form>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
      {/* go to chatbot 972559518883 */}
      <a
        href="https://wa.me/972559518883"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.chatbotLink}
      >
        {whatsappIcon}
        מעבר לצ'אט בוט
      </a>
    </div>
  );
};

export default Login;
